import React, { useCallback, useRef, useState } from 'react';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from './button';

const STATUS = {
  idle: 'idle',
  loading: 'loading',
  done: 'done',
};

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Button with idle → loading (spinner) → Done states for async form submits.
 *
 * onAction receives { beginLoading } — call beginLoading() only after validation passes,
 * so the button stays enabled when validation fails.
 */
export default function AsyncSubmitButton({
  onAction,
  onSuccess,
  onStatusChange,
  loadingLabel = 'Saving…',
  doneLabel = 'Done',
  doneDelay = 700,
  children,
  disabled,
  ...buttonProps
}) {
  const [status, setStatus] = useState(STATUS.idle);
  const lockRef = useRef(false);

  const setStatusAndNotify = useCallback((next) => {
    setStatus(next);
    onStatusChange?.(next);
  }, [onStatusChange]);

  const handleClick = async () => {
    if (lockRef.current || status !== STATUS.idle || disabled) return;

    lockRef.current = true;
    let beganLoading = false;
    try {
      await onAction({
        beginLoading: () => {
          beganLoading = true;
          setStatusAndNotify(STATUS.loading);
        },
      });
      if (!beganLoading) return;

      setStatusAndNotify(STATUS.done);
      await sleep(doneDelay);
      onSuccess?.();
      setStatusAndNotify(STATUS.idle);
    } catch {
      setStatusAndNotify(STATUS.idle);
    } finally {
      lockRef.current = false;
    }
  };

  const isBusy = status !== STATUS.idle;

  return (
    <Button
      type="button"
      disabled={disabled || isBusy}
      onClick={handleClick}
      {...buttonProps}
    >
      {status === STATUS.loading && (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          {loadingLabel}
        </>
      )}
      {status === STATUS.done && (
        <>
          <CheckCircle2 className="w-4 h-4" />
          {doneLabel}
        </>
      )}
      {status === STATUS.idle && children}
    </Button>
  );
}

export { STATUS as ASYNC_SUBMIT_STATUS };
